import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { DarkModeProvider } from './contexts/DarkModeContext';
import { landingPageContent } from '../content/landingPageContent';

const PartnershipModel = lazy(() =>
  import('./components/PartnershipModel').then((module) => ({
    default: module.PartnershipModel,
  })),
);
const SolutionsTailored = lazy(() =>
  import('./components/SolutionsTailored').then((module) => ({
    default: module.SolutionsTailored,
  })),
);
const VisionMission = lazy(() =>
  import('./components/VisionMission').then((module) => ({
    default: module.VisionMission,
  })),
);
const Services = lazy(() =>
  import('./components/Services').then((module) => ({
    default: module.Services,
  })),
);
const AnswerEngine = lazy(() =>
  import('./components/AnswerEngine').then((module) => ({
    default: module.AnswerEngine,
  })),
);
const Approach = lazy(() =>
  import('./components/Approach').then((module) => ({
    default: module.Approach,
  })),
);
const Presence = lazy(() =>
  import('./components/Presence').then((module) => ({
    default: module.Presence,
  })),
);
const TrackRecord = lazy(() =>
  import('./components/TrackRecord').then((module) => ({
    default: module.TrackRecord,
  })),
);
const Partners = lazy(() =>
  import('./components/Partners').then((module) => ({
    default: module.Partners,
  })),
);
const Team = lazy(() =>
  import('./components/Team').then((module) => ({
    default: module.Team,
  })),
);
const TrustStrip = lazy(() =>
  import('./components/TrustStrip').then((module) => ({
    default: module.TrustStrip,
  })),
);
const Insights = lazy(() =>
  import('./components/Insights').then((module) => ({
    default: module.Insights,
  })),
);
const Testimonials = lazy(() =>
  import('./components/Testimonials').then((module) => ({
    default: module.Testimonials,
  })),
);
const Contact = lazy(() =>
  import('./components/Contact').then((module) => ({
    default: module.Contact,
  })),
);
const Footer = lazy(() =>
  import('./components/Footer').then((module) => ({
    default: module.Footer,
  })),
);

function DeferredSection({
  id,
  children,
  minHeight = 420,
}: {
  id?: string;
  children: ReactNode;
  minHeight?: number;
}) {
  const [shouldLoad, setShouldLoad] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (shouldLoad) {
      return;
    }

    if (!('IntersectionObserver' in window)) {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '0px 0px -160px 0px', threshold: 0.01 },
    );
    const currentRef = ref.current;

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => observer.disconnect();
  }, [shouldLoad]);

  return (
    <div id={id} ref={ref} style={shouldLoad ? undefined : { minHeight }}>
      {shouldLoad ? (
        <Suspense fallback={<div style={{ minHeight }} aria-hidden="true" />}>
          {children}
        </Suspense>
      ) : null}
    </div>
  );
}

export default function App() {
  const content = landingPageContent;

  return (
    <DarkModeProvider>
      <div className="min-h-screen bg-white dark:bg-[#100f14] transition-colors duration-300">
        <Navbar />
        {content.hero.enabled && <Hero />}
        {content.partnershipModel.enabled && (
          <DeferredSection
            id={content.partnershipModel.sectionId}
            minHeight={580}
          >
            <PartnershipModel />
          </DeferredSection>
        )}
        {content.solutionsTailored.enabled && (
          <DeferredSection id="mena" minHeight={620}>
            <SolutionsTailored />
          </DeferredSection>
        )}
        {content.visionMission.enabled && (
          <DeferredSection id="vision-mission">
            <VisionMission />
          </DeferredSection>
        )}
        {content.services.enabled && (
          <DeferredSection id="services" minHeight={680}>
            <Services />
          </DeferredSection>
        )}
        {content.answerEngine.enabled && (
          <DeferredSection id={content.answerEngine.sectionId} minHeight={620}>
            <AnswerEngine />
          </DeferredSection>
        )}
        {content.approach.enabled && (
          <DeferredSection id={content.approach.sectionId}>
            <Approach />
          </DeferredSection>
        )}
        {content.presence.enabled && (
          <DeferredSection id={content.presence.sectionId}>
            <Presence />
          </DeferredSection>
        )}
        {content.trackRecord.enabled && (
          <DeferredSection id={content.trackRecord.sectionId} minHeight={820}>
            <TrackRecord />
          </DeferredSection>
        )}
        <DeferredSection id="partners" minHeight={360}>
          <Partners />
        </DeferredSection>
        {content.team.enabled && (
          <DeferredSection id={content.team.sectionId} minHeight={620}>
            <Team />
          </DeferredSection>
        )}
        {content.trustStrip.enabled && (
          <DeferredSection minHeight={180}>
            <TrustStrip />
          </DeferredSection>
        )}
        {content.insights.enabled && (
          <DeferredSection id="insights">
            <Insights />
          </DeferredSection>
        )}
        {content.testimonials.enabled && (
          <DeferredSection id="testimonials">
            <Testimonials />
          </DeferredSection>
        )}
        {content.contact.enabled && (
          <DeferredSection id="contact" minHeight={720}>
            <Contact />
          </DeferredSection>
        )}
        {content.footer.enabled && (
          <DeferredSection minHeight={360}>
            <Footer />
          </DeferredSection>
        )}
      </div>
    </DarkModeProvider>
  );
}
